import { Link as RouterLink, useLocation, useMatch } from "react-router-dom";
import { useHover } from "react-use";
import { Box, Checkbox, Link, Typography as T } from "@mui/material";
import Chip from "catalog/components/Chip";
import EntityTag from "catalog/components/EntityTag";
import MatchHighlighter from "catalog/components/MatchHighlighter";
import PinButton from "catalog/components/PinButton";
import PreferredBadge from "catalog/components/PreferredBadge";
import ResultDescription from "catalog/components/ResultDescription";
import ResultMatches from "catalog/components/ResultMatches";
import { PREFERRED_ENTITIES_TAG } from "catalog/utils/buildFilterMatrix";
import TextOverflow from "~/components/TextOverflow";
import { Highlight, TableEntity } from "~/types/interfaces/catalogResponse";

type ResultProps = {
  entity: TableEntity;
  highlight?: Highlight;
  selected?: boolean;
  selectable?: boolean;
  toggleSelected?: (checked: boolean) => void;
};

const DatasetResult = ({
  entity,
  highlight,
  selected,
  selectable,
  toggleSelected,
}: ResultProps) => {
  const match = useMatch("/data-browser/:tab/*");
  const { search } = useLocation();
  const { name, tags, description } = entity;
  const isPreferred = tags?.some((tag) => tag.tagFQN === PREFERRED_ENTITIES_TAG);
  const tableTags = tags?.filter((tag) => tag.tagFQN !== PREFERRED_ENTITIES_TAG);

  const row = (hovered: boolean) => (
    <Box
      data-testid="dataset-result"
      sx={{
        display: "flex",
        py: 2.5,
        gap: 0.75,
        fontSize: 14,
        position: "relative",
        textDecoration: "none",
        color: "initial",
        flexDirection: "column",
        "&:not(:first-of-type)": {
          borderTop: "1px solid",
          borderTopColor: "grey.300",
        },
      }}
    >
      {/**
       * Selection & Pinning controls
       */}
      <Box
        sx={{
          position: "absolute",
          left: -40,
          top: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 38,
          height: 33,
        }}
      >
        {!selectable && <PinButton fqn={entity.fullyQualifiedName} showPin={hovered} />}
        {selectable && toggleSelected && (
          <Checkbox
            checked={selected}
            size="small"
            onChange={(event) => toggleSelected(event.target.checked)}
          />
        )}
      </Box>

      <Link
        underline="hover"
        component={RouterLink}
        to={{
          pathname: `/data-browser/tables/${entity.fullyQualifiedName}`,
          search: search,
        }}
        state={{ referrer: match?.params?.tab }}
        sx={{
          lineHeight: "33px",
          maxWidth: "fit-content",
          fontWeight: 600,
          color: "text.link",
          display: "flex",
          alignItems: "center",
          gap: 1,
          "&:visited": {
            color: "text.visited",
          },
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        <TextOverflow fontWeight={600} fontSize={16}>
          <MatchHighlighter text={name} highlight={highlight?.name} />
        </TextOverflow>
        {isPreferred && <PreferredBadge />}
      </Link>

      {/**
       * Description
       */}
      {description && (
        <ResultDescription description={description} highlight={highlight?.description} />
      )}
      {!description && (
        <T component="div" fontSize={14} fontStyle="italic" color="grey.600">
          No description
        </T>
      )}

      {/**
       * Dataset tags
       */}
      {!!tableTags?.length && (
        <Box sx={{ display: "flex", pt: 0.5, gap: 0.5, flexFlow: "wrap", alignItems: "center" }}>
          {tableTags.map((tag) => (
            <Chip key={tag.tagFQN} sx={{ py: 0.5 }}>
              <EntityTag tag={tag} />
            </Chip>
          ))}
        </Box>
      )}

      {highlight && <ResultMatches highlight={highlight} />}
    </Box>
  );

  const [hoverable] = useHover(row);
  return hoverable;
};

export default DatasetResult;
